import { useAuth } from '@/hooks/useAuth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, UserCog, Search, Shield, Ban, Trash2, KeyRound, Save, AlertTriangle, ShieldCheck, ShieldOff, Mail,
  UserX, Globe, Lock, RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  searchUsersByNickname, adminGetUserProfile, adminUpdateUserProfile,
  adminSetUserPassword, adminSetUserBanned, adminDeleteUser,
  admin_getUsersNotInServer, admin_purgeUsersNotInServer, type UserNotInServer,
} from '@/lib/actions';
import type { UserProfile } from '@/lib/types';

type FoundUser = { id: string; nickname: string; profileImageUrl: string | null };

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>;
}

function UserEditor({ uid, onClose, onChanged }: { uid: string; onClose: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [email, setEmail] = useState('');
  const [banned, setBanned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({ nickname: '', bio: '', role: 'user', discordId: '', minecraftId: '' });
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setLoading(true);
    adminGetUserProfile(uid).then((res) => {
      if (res.success && res.profile) {
        setProfile(res.profile);
        setForm({
          nickname: res.profile.nickname || '',
          bio: res.profile.bio || '',
          role: res.profile.role || 'user',
          discordId: res.profile.discordId || '',
          minecraftId: res.profile.minecraftId || '',
        });
        setEmail(res.auth?.email || res.profile.email || '');
        setBanned(!!res.profile.banned);
        setBanReason(res.profile.banReason || '');
      } else {
        toast({ title: 'Error', description: res.message, variant: 'destructive' });
      }
      setLoading(false);
    });
  }, [uid]);

  const handleSave = async () => {
    setSaving(true);
    const res = await adminUpdateUserProfile(uid, { ...form, role: form.role as any, email });
    if (res.success) { toast({ title: 'Guardado', description: res.message }); onChanged(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setSaving(false);
  };

  const handlePassword = async () => {
    if (pwd.length < 6) { toast({ title: 'Contraseña muy corta', description: 'Mínimo 6 caracteres.', variant: 'destructive' }); return; }
    setPwdSaving(true);
    const res = await adminSetUserPassword(uid, pwd);
    if (res.success) { toast({ title: 'Contraseña cambiada', description: res.message }); setPwd(''); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setPwdSaving(false);
  };

  const handleBan = async (next: boolean) => {
    setBusy(true);
    const res = await adminSetUserBanned(uid, next, banReason);
    if (res.success) { toast({ title: next ? 'Usuario baneado' : 'Usuario desbaneado', description: res.message }); setBanned(next); onChanged(); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setBusy(false);
  };

  const handleDelete = async () => {
    setBusy(true);
    const res = await adminDeleteUser(uid);
    if (res.success) { toast({ title: 'Usuario eliminado', description: res.message }); onChanged(); onClose(); }
    else { toast({ title: 'Error', description: res.message, variant: 'destructive' }); setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-white/8">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11 border border-white/10"><AvatarImage src={profile?.profileImageUrl} /><AvatarFallback>{(form.nickname || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar>
            <div className="min-w-0">
              <SheetTitle className="truncate flex items-center gap-2">
                {form.nickname || 'Usuario'}
                {banned && <Badge className="bg-red-500/20 text-red-400">Baneado</Badge>}
                {form.role === 'admin' && <Badge className="bg-amber-500/20 text-amber-400">Admin</Badge>}
              </SheetTitle>
              <SheetDescription className="truncate">{email}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="px-6 py-5 space-y-6">
              {/* Datos */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Nickname"><Input value={form.nickname} onChange={(e) => set('nickname', e.target.value)} /></Field>
                  <Field label={<span className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</span>}><Input value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Discord ID"><Input value={form.discordId} onChange={(e) => set('discordId', e.target.value)} /></Field>
                  <Field label="Minecraft ID"><Input value={form.minecraftId} onChange={(e) => set('minecraftId', e.target.value)} /></Field>
                </div>
                <Field label="Rol">
                  <Select value={form.role} onValueChange={(v) => set('role', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Usuario</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Biografía"><RichTextarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} /></Field>
                <Button onClick={handleSave} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar cambios
                </Button>
              </div>

              {/* Contraseña */}
              <div className="rounded-2xl border border-white/8 bg-card/40 p-4 space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold"><KeyRound className="h-4 w-4 text-amber-400" /> Cambiar contraseña</p>
                <div className="flex gap-2">
                  <Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Nueva contraseña (mín. 6)" />
                  <Button onClick={handlePassword} disabled={pwdSaving || pwd.length < 6} variant="secondary">
                    {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cambiar'}
                  </Button>
                </div>
              </div>

              {/* Baneo */}
              <div className="rounded-2xl border border-orange-500/30 bg-orange-500/5 p-4 space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-orange-400"><Ban className="h-4 w-4" /> Baneo</p>
                <Field label="Motivo (opcional)"><Input value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder="Motivo del baneo..." /></Field>
                {banned ? (
                  <Button onClick={() => handleBan(false)} disabled={busy} variant="secondary" className="gap-2">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Desbanear usuario
                  </Button>
                ) : (
                  <Button onClick={() => handleBan(true)} disabled={busy} className="gap-2 bg-orange-600 hover:bg-orange-700">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />} Banear usuario
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">Un usuario baneado verá una pantalla roja y no podrá acceder a nada de Jartinet desde ninguna ruta.</p>
              </div>

              {/* Zona peligrosa */}
              <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-destructive mb-1"><AlertTriangle className="h-4 w-4" /> Zona peligrosa</p>
                <p className="text-xs text-muted-foreground mb-3">Eliminar el usuario borra su cuenta de acceso y su perfil de forma permanente.</p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={busy} className="gap-2"><Trash2 className="h-4 w-4" /> Eliminar usuario</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar a "{form.nickname}"?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción es permanente. Se borrará su cuenta de acceso (Auth) y su perfil.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function descargarTxt(nombre: string, contenido: string) {
  const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const KILL_TITLE_PURGA = 'MUNDO NO AUTORIZADO';
const KILL_MESSAGE_PURGA = 'Este equipo no está autorizado para ejecutar Mod Cofrade. Contacta con el autor.';

/**
 * Usuarios de Jartinet que nunca han entrado al servidor. Lo calcula la CF
 * `admin_usersNotInServer` cruzando Actividad (tiempo por cuenta vinculada o por
 * nombre de Minecraft del perfil/formularios), la vinculación del mod
 * (`mc_uuid`) y, para sus mundos privados, Seguridad del mod.
 */
function UsuariosSinEntrar({ onOpen }: { onOpen: (uid: string) => void }) {
  const { toast } = useToast();
  const [cargando, setCargando] = useState(true);
  const [lista, setLista] = useState<UserNotInServer[]>([]);
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());
  const [borrando, setBorrando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    const res = await admin_getUsersNotInServer();
    if (!res.success) toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setLista(res.users);
    setExcluidos(new Set());
    setCargando(false);
  };

  useEffect(() => { cargar(); /* una vez al entrar */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seleccionados = lista.filter((u) => !excluidos.has(u.uid));
  const mundos = seleccionados.reduce((n, u) => n + u.worlds.length, 0);
  const sinDiscord = seleccionados.filter((u) => !u.discordId).length;

  const alternar = (uid: string) => setExcluidos((prev) => {
    const s = new Set(prev);
    if (s.has(uid)) s.delete(uid); else s.add(uid);
    return s;
  });

  const purgar = async () => {
    if (seleccionados.length === 0) return;
    setBorrando(true);
    // Primero el .txt: después del borrado ya no quedaría de dónde sacar los IDs.
    const ids = [...new Set(seleccionados.map((u) => u.discordId).filter(Boolean))];
    descargarTxt(`discord-ids-sin-entrar-${new Date().toISOString().slice(0, 10)}.txt`, ids.join('\n') + (ids.length ? '\n' : ''));
    const res = await admin_purgeUsersNotInServer(seleccionados.map((u) => u.uid), KILL_TITLE_PURGA, KILL_MESSAGE_PURGA);
    if (res.success) {
      toast({
        title: 'Usuarios eliminados',
        description: `${res.deleted} usuarios borrados · ${res.lockedWorlds} mundos en lockdown · ${ids.length} Discord ID en el .txt${res.errors.length ? ` · ${res.errors.length} errores` : ''}`,
      });
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setBorrando(false);
    cargar();
  };

  return (
    <section className="space-y-4 rounded-3xl border border-rose-500/25 bg-rose-500/5 p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-headline text-2xl font-bold">
            <UserX className="h-6 w-6 text-rose-400" /> Usuarios que no han entrado al servidor
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Cuentas de Jartinet sin ningún rastro en el servidor: sin tiempo en Actividad (ni por cuenta vinculada ni por
            el nombre de Minecraft de su perfil o de sus formularios) y sin haber usado <code>/vincular</code>. Los admins
            no salen. Sus mundos privados se buscan en Seguridad del mod por ese mismo nombre.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={cargar} disabled={cargando || borrando} className="gap-1.5">
          <RefreshCw className={cn('h-4 w-4', cargando && 'animate-spin')} /> Recalcular
        </Button>
      </div>

      {cargando ? (
        <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-rose-400" /></div>
      ) : lista.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">
          Todos los usuarios de Jartinet han entrado al servidor.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <p className="min-w-0 flex-1 text-sm">
              <strong>{seleccionados.length}</strong> de {lista.length} seleccionados · <strong>{mundos}</strong> mundo{mundos === 1 ? '' : 's'} privado{mundos === 1 ? '' : 's'}
              {sinDiscord > 0 && <span className="text-amber-300"> · {sinDiscord} sin Discord ID</span>}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={borrando || seleccionados.length === 0} className="gap-2">
                  {borrando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Eliminar {seleccionados.length} y hacer lockdown a sus mundos
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar {seleccionados.length} usuarios de Jartinet?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se borrará su cuenta de acceso y su perfil de forma permanente y se hará lockdown a {mundos} mundo{mundos === 1 ? '' : 's'} privado{mundos === 1 ? '' : 's'}.
                    Antes se descargará un .txt con los Discord ID de todos ellos.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={purgar} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
            {lista.map((u) => {
              const fuera = excluidos.has(u.uid);
              return (
                <div key={u.uid} className={cn('flex items-start gap-3 rounded-2xl border p-3', fuera ? 'border-white/5 bg-card/20 opacity-50' : 'border-white/8 bg-card/40')}>
                  <input
                    type="checkbox"
                    checked={!fuera}
                    onChange={() => alternar(u.uid)}
                    className="mt-1 h-4 w-4 shrink-0 accent-rose-500"
                    title={fuera ? 'Incluir' : 'Dejar fuera'}
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <button onClick={() => onOpen(u.uid)} className="truncate text-left font-semibold hover:underline">
                        {u.nickname || u.email || u.uid}
                      </button>
                      {u.banned && <Badge className="bg-red-500/20 text-red-400">Baneado</Badge>}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{u.email || '—'}</p>
                    <p className="text-xs">
                      <span className="text-muted-foreground">MC:</span> {u.minecraftNames.join(', ') || '—'}
                      <span className="text-muted-foreground"> · Discord:</span>{' '}
                      {u.discordId || <span className="text-amber-300">sin ID</span>}
                    </p>
                    {u.worlds.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {u.worlds.map((w) => (
                          <span key={w.id} className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem]', w.authorized ? 'bg-sky-500/15 text-sky-300' : 'bg-rose-500/15 text-rose-300')}>
                            {w.authorized ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                            {w.worldName || w.account}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

export default function AdminUsuariosPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { users } = await searchUsersByNickname(q);
      setResults(users || []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [term, refreshKey]);

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-rose-500/15 via-card/40 to-card/20 p-8 md:p-10"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-rose-500/20 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-rose-500/40 blur-xl" />
            <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-rose-400/30 bg-rose-500/15 text-rose-300"><UserCog className="h-9 w-9" /></div>
          </div>
          <div>
            <h1 className="font-headline text-4xl font-bold md:text-5xl">Gestión de Usuarios</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">Edita la información de cualquier usuario, cambia su contraseña, banéalo o elimínalo.</p>
          </div>
        </div>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario por nombre..." className="pl-9" />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {results.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
          <Shield className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">{term.trim().length < 2 ? 'Escribe al menos 2 letras para buscar.' : 'Sin resultados.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {results.map((u) => (
            <button
              key={u.id}
              onClick={() => setEditingUid(u.id)}
              className="group flex items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-rose-400/30"
            >
              <Avatar className="h-11 w-11 border border-white/10"><AvatarImage src={u.profileImageUrl || undefined} /><AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{u.nickname}</p>
                <p className="text-xs text-muted-foreground">Pulsa para gestionar</p>
              </div>
              <UserCog className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      )}

      <UsuariosSinEntrar onOpen={setEditingUid} />

      {editingUid && (
        <UserEditor
          uid={editingUid}
          onClose={() => setEditingUid(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
